/** Shared tenant → integration → account selector for the data-sync pages.
 *
 * Integrations come from the tenant's configured rows
 * (`/tour_operators/{id}/integrations/`, booking kind) intersected with the
 * feature flags of the global catalog (`/integrations/` → `supports`), so a
 * page only offers restechs that actually support its flow.
 */
import { Box, MenuItem, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import * as integrationsApi from '@/api/integrations';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { getTenantScope } from '@/scope/tenantScope';

export interface SyncScope {
  tenant: TenantPickerValue | null;
  integration: string | null;
  /** Account slug; null = the integration's default account. */
  account: string | null;
}

export const EMPTY_SCOPE: SyncScope = { tenant: null, integration: null, account: null };

/** Starting scope for a sync page: the console-wide tenant when one is set.
 *  Seeded ONCE, at mount — these pages carry dependent integration/account
 *  choices, and re-seeding mid-flight would silently discard them. */
export function initialSyncScope(): SyncScope {
  return { ...EMPTY_SCOPE, tenant: getTenantScope() };
}

interface Props {
  value: SyncScope;
  onChange: (scope: SyncScope) => void;
  /** Feature the restech must support (from IntegrationFactory.list_features). */
  requiredFeature: 'booking_sync' | 'order_sync' | 'product_list' | 'product_sync';
}

/** Global integration catalog keyed by restech id (features + booking_query). */
export function useIntegrationCatalog(): Record<string, integrationsApi.Integration> {
  const catalog = useQuery({
    queryKey: ['integrations-catalog'],
    queryFn: () => integrationsApi.list(),
    staleTime: 5 * 60_000,
  });
  return useMemo(() => {
    const out: Record<string, integrationsApi.Integration> = {};
    for (const entry of catalog.data?.results ?? []) out[entry.id] = entry;
    return out;
  }, [catalog.data]);
}

export function useIntegrationFeatures(): Record<string, string[]> {
  const catalog = useIntegrationCatalog();
  return useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const [id, entry] of Object.entries(catalog)) out[id] = entry.supports;
    return out;
  }, [catalog]);
}

/** The tenant's configured integration rows. Shared cache with the picker. */
function useIntegrationRows(tenantId: number | undefined) {
  return useQuery({
    queryKey: ['to-integrations', tenantId],
    queryFn: () => integrationsApi.listRows(tenantId!),
    enabled: Boolean(tenantId),
  });
}

/** The single booking row a scope resolves to, or null.
 *
 *  Carries `source_id` — the Import_Source code this integration writes under,
 *  already resolved by the backend including the fallback where a named account
 *  with no source of its own inherits the default account's
 *  (`integrations_manage.py::_booking_row`). A page that needs to show what a
 *  run will do reads it from here rather than fetching anything.
 */
export function useIntegrationRow(scope: SyncScope): integrationsApi.IntegrationRow | null {
  const rows = useIntegrationRows(scope.tenant?.id);
  return useMemo(() => {
    if (!scope.integration) return null;
    return (
      (rows.data?.results ?? []).find(
        (r) =>
          r.kind === 'booking' &&
          r.restech === scope.integration &&
          (r.slug ?? null) === (scope.account ?? null),
      ) ?? null
    );
  }, [rows.data, scope.integration, scope.account]);
}

export function SyncScopePicker({ value, onChange, requiredFeature }: Props) {
  const catalog = useIntegrationCatalog();

  const rows = useIntegrationRows(value.tenant?.id);

  const bookingRows = useMemo(
    () =>
      (rows.data?.results ?? []).filter(
        (r) =>
          r.kind === 'booking' &&
          r.restech &&
          r.enabled &&
          (catalog[r.restech]?.supports ?? []).includes(requiredFeature) &&
          // The sync launchers can't drive integrations whose sync flow is
          // incompatible with the bg-task flow.
          (requiredFeature !== 'booking_sync' ||
            catalog[r.restech]?.booking_query?.ui_launchable !== false) &&
          (requiredFeature !== 'order_sync' ||
            catalog[r.restech]?.order_query?.ui_launchable !== false),
      ),
    [rows.data, catalog, requiredFeature],
  );

  const restechs = useMemo(
    () => Array.from(new Set(bookingRows.map((r) => r.restech as string))),
    [bookingRows],
  );
  const accounts = useMemo(
    () => bookingRows.filter((r) => r.restech === value.integration),
    [bookingRows, value.integration],
  );

  return (
    <>
      <Box sx={{ width: 280 }}>
        <TenantPicker
          placeholder="Choose a tour operator"
          value={value.tenant}
          onChange={(t) => onChange({ tenant: t, integration: null, account: null })}
        />
      </Box>
      <TextField
        select
        size="small"
        label="Integration"
        value={value.integration ?? ''}
        onChange={(e) => onChange({ ...value, integration: e.target.value || null, account: null })}
        disabled={!value.tenant || rows.isLoading}
        helperText={
          value.tenant && !rows.isLoading && restechs.length === 0
            ? `No configured integration supports ${requiredFeature.replace('_', ' ')}`
            : undefined
        }
        sx={{ minWidth: 220 }}
      >
        {restechs.map((r) => (
          <MenuItem key={r} value={r}>
            {r}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Account"
        value={value.account ?? ''}
        onChange={(e) => onChange({ ...value, account: e.target.value || null })}
        disabled={!value.integration}
        sx={{ minWidth: 180 }}
      >
        {accounts.map((r) => (
          <MenuItem key={r.id} value={r.slug ?? ''}>
            {r.slug ? r.display_name || r.slug : 'Default'}
          </MenuItem>
        ))}
      </TextField>
    </>
  );
}
